/**
 * EntityFilterContext.js
 * 
 * Generic filter context that extends BaseFilterContext with tab management.
 * This context provides tab functionality for any entity type (projects, collections, etc.).
 * 
 * It wraps BaseFilterProvider and adds:
 * - activeTab state management
 * - Tab change handlers
 * - Tab-related utilities
 */

import React, { createContext, useState, useContext, useCallback, useMemo } from 'react';
import { BaseFilterProvider, useBaseFilter } from './BaseFilterContext';
import { TAB_NAMES } from '../../utils/accessMapConstants';

// ============================================================================
// CONTEXT CREATION AND EXPORTS
// ============================================================================

const EntityFilterContext = createContext();

export const useEntityFilter = () => useContext(EntityFilterContext);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

/**
 * Internal component that uses BaseFilterContext and adds tab management
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.entityType - Entity type (e.g., 'project', 'collection')
 * @param {string} [props.defaultTab] - Default active tab (defaults to 'myItems' or 'myProjects')
 */
const EntityFilterProviderInner = ({ children, entityType, defaultTab }) => {
  // Get base filter functionality from BaseFilterContext
  const baseFilter = useBaseFilter();
  
  // Determine default tab based on entity type
  const getDefaultTab = () => {
    if (defaultTab) return defaultTab;
    // Use 'myProjects' for projects, 'myItems' for other entities
    return entityType === 'project' ? TAB_NAMES.MY_PROJECTS : TAB_NAMES.MY_ITEMS;
  };
  
  // Active tab state - determines which view is currently displayed
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  
  // ============================================================================
  // TAB MANAGEMENT FUNCTIONS
  // ============================================================================
  
  /**
   * Changes the active tab
   * @param {string} tab - The tab ID to switch to
   */
  const changeActiveTab = useCallback((tab) => {
    if (tab && typeof tab === 'string') {
      setActiveTab(tab);
    }
  }, []);
  
  /**
   * Resets the active tab to default
   */
  const resetActiveTab = useCallback(() => {
    setActiveTab(getDefaultTab());
  }, []);
  
  // ============================================================================
  // CONTEXT VALUE CREATION
  // ============================================================================
  
  // Create the context value that merges base filter with tab management
  const contextValue = useMemo(() => ({
    // ============================================================================
    // FILTER STATE (from BaseFilterContext)
    // ============================================================================
    selectedTags: baseFilter.selectedTags,
    setSelectedTags: baseFilter.setSelectedTags,
    selectedStatuses: baseFilter.selectedStatuses,
    selectedVisibilities: baseFilter.selectedVisibilities,
    searchByKeyword: baseFilter.searchByKeyword,
    searchByTitle: baseFilter.searchByTitle,
    searchByDescription: baseFilter.searchByDescription,
    searchByTag: baseFilter.searchByTag,
    dateFilterMode: baseFilter.dateFilterMode,
    queryFromDay: baseFilter.queryFromDay,
    queryToDay: baseFilter.queryToDay,
    isDateFilterActive: baseFilter.isDateFilterActive,
    
    // ============================================================================
    // TAB STATE
    // ============================================================================
    activeTab,                       // Currently active tab
    
    // ============================================================================
    // FILTER MANAGEMENT FUNCTIONS (from BaseFilterContext)
    // ============================================================================
    clearAllFilters: baseFilter.clearAllFilters,
    clearSearchFilters: baseFilter.clearSearchFilters,
    clearStatusFilters: baseFilter.clearStatusFilters,
    clearVisibilityFilters: baseFilter.clearVisibilityFilters,
    clearDateFilters: baseFilter.clearDateFilters,
    
    // ============================================================================
    // SEARCH FUNCTIONS (from BaseFilterContext)
    // ============================================================================
    setSearchByKeyword: baseFilter.setSearchByKeyword,
    setSearchByTitle: baseFilter.setSearchByTitle,
    setSearchByDescription: baseFilter.setSearchByDescription,
    setSearchByTag: baseFilter.setSearchByTag,
    
    // ============================================================================
    // STATUS FILTER FUNCTIONS (from BaseFilterContext)
    // ============================================================================
    setSelectedStatuses: baseFilter.setSelectedStatuses,
    toggleStatusSelection: baseFilter.toggleStatusSelection,
    
    // ============================================================================
    // VISIBILITY FILTER FUNCTIONS (from BaseFilterContext)
    // ============================================================================
    setSelectedVisibilities: baseFilter.setSelectedVisibilities,
    toggleVisibilitySelection: baseFilter.toggleVisibilitySelection,
    
    // ============================================================================
    // DATE FILTER FUNCTIONS (from BaseFilterContext)
    // ============================================================================
    setDateFilterMode: baseFilter.setDateFilterMode,
    setQueryFromDay: baseFilter.setQueryFromDay,
    setQueryToDay: baseFilter.setQueryToDay,
    setIsDateFilterActive: baseFilter.setIsDateFilterActive,
    
    // ============================================================================
    // TAB MANAGEMENT FUNCTIONS
    // ============================================================================
    setActiveTab: changeActiveTab,  // Function to change active tab
    resetActiveTab,                  // Function to reset tab to default
    
    // ============================================================================
    // UTILITY PROPERTIES
    // ============================================================================
    entityType,                      // Entity type for reference
    TAB_NAMES,                       // Tab name constants for convenience
  }), [
    baseFilter,
    activeTab,
    changeActiveTab,
    resetActiveTab,
    entityType,
  ]);

  return (
    <EntityFilterContext.Provider value={contextValue}>
      {children}
    </EntityFilterContext.Provider>
  );
};

/**
 * Outer provider that wraps BaseFilterProvider
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.entityType - Entity type (e.g., 'project', 'collection')
 * @param {string} [props.defaultTab] - Default active tab
 */
export const EntityFilterProvider = ({ children, entityType, defaultTab }) => {
  return (
    <BaseFilterProvider entityType={entityType}>
      <EntityFilterProviderInner entityType={entityType} defaultTab={defaultTab}>
        {children}
      </EntityFilterProviderInner>
    </BaseFilterProvider>
  );
};

